import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { catchError, of, tap } from 'rxjs';
import { Modal } from 'src/app/@domain/modal/modal.enum';
import { ModalService } from 'src/app/@domain/modal/modal.service';
import { UserService } from 'src/app/@domain/user/application/user.service';
import { equalsValidator } from '../../../../../../@common/form/equals.validator';
import { strongPasswordValidator } from '../../../../../../@common/form/strong-password.validator';
import { NotificationService } from '../../../../../../@domain/notification/application/notification.service';
import { ErrorComponent } from '../../../../global/error/error.component';
import { SpinnerComponent } from '../../../../global/spinner/spinner.component';
import { DeleteAccountModalComponent } from './delete-account-modal.component';

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: [ './user-edit.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ FormsModule, ErrorComponent, SpinnerComponent, ReactiveFormsModule, RouterLink, FaIconComponent, DeleteAccountModalComponent ]
})
export class UserEditComponent {
    private userService = inject(UserService);
    private notificationService = inject(NotificationService);
    private modalService = inject(ModalService);
    private router = inject(Router);
    private fb = inject(FormBuilder);

    user = this.userService.user;

    profileForm = this.fb.group({
        firstName: [ '', [ Validators.required ] ],
        lastName: [ '', [ Validators.required ] ],
        email: [ { value: '', disabled: true } ],
    });

    passwordForm = this.fb.group({
        oldPassword: [ '', [ Validators.required ] ],
        newPassword: [ '', [ Validators.required, strongPasswordValidator ] ],
        rePassword: [ '', [ Validators.required ] ],
    }, { validators: [ equalsValidator('newPassword', 'rePassword') ] });

    errorMessage = signal('');
    isLoadingProfile = signal(false);
    isLoadingPassword = signal(false);
    showCurrentPw = signal(false);
    showNewPw = signal(false);
    faEye = faEye;
    faEyeSlash = faEyeSlash;

    constructor() {
        effect(() => {
            const user = this.userService.user();
            this.profileForm.patchValue({
                firstName: user?.first_name,
                lastName: user?.last_name,
                email: user?.email,
            });
        });
    }

    save(): void {
        const passwordSkipped = this.passwordForm.pristine;

        if (this.profileForm.valid) {
            this.isLoadingProfile.set(true);
            this.userService.updateProfile({
                first_name: this.firstName!.value!,
                last_name: this.lastName!.value!,
            }).pipe(
                tap(() => {
                    if (passwordSkipped) {
                        this.notificationService.success({ detail: 'Profile updated successfully!' });
                    }
                }),
                catchError(() => of(null))
            ).subscribe(() => this.isLoadingProfile.set(false));
        }

        if (!this.passwordForm.pristine && this.passwordForm.valid) {
            this.isLoadingPassword.set(true);
            this.userService.updatePassword({
                old_password: this.oldPassword!.value!,
                new_password: this.newPassword!.value!,
            }).pipe(
                tap(() => this.notificationService.success({ detail: 'Profile updated successfully!' })),
                catchError(() => {
                    this.errorMessage.set('Invalid password provided!');
                    return of(null);
                })
            ).subscribe(() => {
                this.passwordForm.reset();
                this.isLoadingPassword.set(false);
            });
        }
    }

    discard(): void {
        this.router.navigate([ '/profile' ]);
    }

    confirmDelete(): void {
        this.modalService.open(Modal.DeleteAccount);
    }

    get firstName() { return this.profileForm.get('firstName'); }
    get lastName() { return this.profileForm.get('lastName'); }
    get oldPassword() { return this.passwordForm.get('oldPassword'); }
    get newPassword() { return this.passwordForm.get('newPassword'); }
    get rePassword() { return this.passwordForm.get('rePassword'); }
}
